import { fetchAuthSession } from "aws-amplify/auth";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  "https://f0usr04663.execute-api.us-east-1.amazonaws.com";

export type PaymentRole = "OWNER" | "INTEGRATOR";
export type PaymentMethodType = "CARD" | "ACSS_DEBIT";
export type CollectionStatus =
  | "NOT_CONFIGURED"
  | "READY"
  | "PROCESSING"
  | "PAID"
  | "FAILED"
  | "ACTION_REQUIRED";

export type SafePaymentMethod = {
  configured: boolean;
  type?: PaymentMethodType | null;
  brand?: string | null;
  bankName?: string | null;
  last4?: string | null;
  expMonth?: number | null;
  expYear?: number | null;
  currency?: "CAD" | "USD" | null;
  status: CollectionStatus;
  updatedAt?: string | null;
};

export type IntegratorPaymentOverview = {
  partnerId: string;
  displayName: string;
  email?: string | null;
  paymentMethod: SafePaymentMethod;
};

export type PaymentMethodsResponse = {
  role: PaymentRole;
  collectionEnabled: boolean;
  generatedAt: string;
  paymentMethod?: SafePaymentMethod;
  integrators?: IntegratorPaymentOverview[];
};

async function authToken() {
  const session = await fetchAuthSession();
  const value = session.tokens?.idToken?.toString();
  if (!value) {
    throw new Error("Your session has expired. Sign out and sign in again.");
  }
  return value;
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await authToken();
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(init.headers || {}),
    },
  });
  const text = await response.text();
  let payload: any = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }
  if (!response.ok) {
    throw new Error(
      payload?.message || text || `Payment request failed (${response.status})`,
    );
  }
  return payload as T;
}

export function getPaymentMethods() {
  return api<PaymentMethodsResponse>("/billing/payment-methods");
}

export function createPaymentMethodSetup(
  paymentMethodType: PaymentMethodType,
  currency: "CAD" | "USD" = "CAD",
) {
  return api<{
    url: string;
    expiresAt?: number | null;
    paymentMethodType: PaymentMethodType;
    currency?: "CAD" | "USD" | null;
  }>(
    "/billing/payment-methods/setup",
    { method: "POST", body: JSON.stringify({ paymentMethodType, currency }) },
  );
}

export function confirmPaymentMethodSetup(sessionId: string) {
  return api<{ paymentMethod: SafePaymentMethod }>(
    "/billing/payment-methods/confirm",
    { method: "POST", body: JSON.stringify({ sessionId }) },
  );
}

export function redirectToStripe(url: string) {
  window.location.assign(url);
}
